import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap'
import Breadcrumbs from '../../../components/Common/Breadcrumb'
import { notifyError } from '../../../utils/notify'
import BudgetDetalhamentoPanel from './BudgetDetalhamentoPanel'
import BudgetItemModal from './BudgetItemModal'
import BudgetCreateContratoModal from './BudgetCreateContratoModal'
import BudgetCardEsteiraBars from './BudgetCardEsteiraBars'
import BudgetEsteiraSummaryCards from './BudgetEsteiraSummaryCards'
import BudgetVisaoGeralChart from './BudgetVisaoGeralChart'
import BudgetVisaoGeralTable from './BudgetVisaoGeralTable'
import {
  buildDrillCards,
  buildDrillNodeFromCard,
  buildItemEsteiraRows,
  DRILL_TYPES,
  getDrillLevel,
  hasDrillEsteiraData,
} from './budgetDrilldownHelpers'
import {
  fetchGestorCcIds,
  loadBudgetDashboardData,
} from './budgetDashboardApi'
import {
  BUDGET_DASHBOARD_DEFAULT_ANO,
  CC_ESTEIRA_STEPS,
  aggregateComprometidoPorCc,
  aggregateDistribuidoPorCc,
  aggregateEsteiraTotais,
  aggregateLiberadoPorCc,
  aggregateOrcadoPorCc,
  aggregatePagoPorCc,
  aggregateProgramadoPorCc,
  buildCcSummaries,
  enrichCcSummaries,
  filterCentrosDeCustoForUser,
  formatBudgetMoney,
  hasBudgetDashboardFullAccess,
  hasCcEsteiraData,
  buildContratosPorItem,
  sortBudgetCards,
} from './budgetDashboardHelpers'

const DRILL_LEVEL_TITLES = {
  [DRILL_TYPES.CC]: 'Centros de custo',
  [DRILL_TYPES.RUBRICA]: 'Rubricas',
  [DRILL_TYPES.SUBRUBRICA]: 'Subrubricas',
  [DRILL_TYPES.ITEM]: 'Itens orçamentários',
}

const CARD_TYPE_BADGES = {
  [DRILL_TYPES.CC]: 'Centro de custo',
  [DRILL_TYPES.RUBRICA]: 'Rubrica',
  [DRILL_TYPES.SUBRUBRICA]: 'Subrubrica',
  [DRILL_TYPES.ITEM]: 'Item',
}

const ANO_DEBOUNCE_MS = 400

const filterInputStyle = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #ced4da',
  minWidth: 160,
}

const sectionTitleStyle = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#273351',
  marginBottom: 16,
}

const viewToggleBtnStyle = (active) => ({
  fontSize: '0.8rem',
  fontWeight: active ? 600 : 400,
})

function currentYear() {
  return new Date().getFullYear()
}

function BudgetDashboard({ auth }) {
  const user = auth?.user
  const [ano, setAno] = useState(BUDGET_DASHBOARD_DEFAULT_ANO)
  const [anoChanging, setAnoChanging] = useState(false)
  const [ccToolbarFilter, setCcToolbarFilter] = useState('')
  const [buscaToolbar, setBuscaToolbar] = useState('')
  const [visaoGeralMode, setVisaoGeralMode] = useState('cards')
  const [graficoMetrica, setGraficoMetrica] = useState('distribuido')
  const [cardsSortBy, setCardsSortBy] = useState('cardNome')
  const [cardsSortDir, setCardsSortDir] = useState('asc')
  const [detalhamentoCcId, setDetalhamentoCcId] = useState('')
  const [detalhamentoOrcItemId, setDetalhamentoOrcItemId] = useState('')
  const [drillPath, setDrillPath] = useState([])
  const [taxonomyRows, setTaxonomyRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [accessResolved, setAccessResolved] = useState(false)
  const [allowedCcIds, setAllowedCcIds] = useState(null)
  const [centros, setCentros] = useState([])
  const [orcCCustoRows, setOrcCCustoRows] = useState([])
  const [dotAnualRows, setDotAnualRows] = useState([])
  const [orcDotRows, setOrcDotRows] = useState([])
  const [eventoRows, setEventoRows] = useState([])
  const [bdroRows, setBdroRows] = useState([])
  const [movRows, setMovRows] = useState([])
  const [contrFornRows, setContrFornRows] = useState([])
  const [loadWarnings, setLoadWarnings] = useState([])
  const [itemModal, setItemModal] = useState({ open: false, orcItemId: null, itemLabel: '' })
  const [createContratoModal, setCreateContratoModal] = useState({ open: false, orcItemId: null })

  const fullAccess = hasBudgetDashboardFullAccess(user)

  useEffect(() => {
    let cancelled = false

    async function resolveAccess() {
      if (!user?.pf_id) {
        if (!cancelled) setAccessResolved(true)
        return
      }
      if (fullAccess) {
        if (!cancelled) {
          setAllowedCcIds(null)
          setAccessResolved(true)
        }
        return
      }
      try {
        const gestorCcIds = await fetchGestorCcIds(user.pf_id)
        if (!cancelled) {
          setAllowedCcIds(gestorCcIds)
          setAccessResolved(true)
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setAllowedCcIds([])
          setAccessResolved(true)
        }
      }
    }

    resolveAccess()
    return () => { cancelled = true }
  }, [user, fullAccess])

  const loadData = useCallback(async () => {
    if (!accessResolved) return
    if (!fullAccess && (!allowedCcIds || allowedCcIds.length === 0)) return

    setLoading(true)
    try {
      const data = await loadBudgetDashboardData({ ano, allowedCcIds })
      setCentros(data.centros || [])
      setOrcCCustoRows(data.orcCCustoRows || [])
      setDotAnualRows(data.dotAnualRows || [])
      setOrcDotRows(data.orcDotRows || [])
      setEventoRows(data.eventoRows || [])
      setBdroRows(data.bdroRows || [])
      setMovRows(data.movRows || [])
      setTaxonomyRows(data.taxonomyRows || [])
      setContrFornRows(data.contrFornRows || [])
      setLoadWarnings(data.loadWarnings || [])

      const semCentros = !(data.centros || []).length
      const semDados = !(data.dotAnualRows || []).length
        && !(data.orcCCustoRows || []).length
        && !(data.orcDotRows || []).length
      if (semCentros && semDados) {
        notifyError('Não foi possível carregar os dados do dashboard de orçamento.')
      }
    } catch (err) {
      console.error(err)
      notifyError('Não foi possível carregar os dados do dashboard de orçamento.')
    } finally {
      setLoading(false)
      setAnoChanging(false)
    }
  }, [accessResolved, fullAccess, allowedCcIds, ano])

  useEffect(() => {
    if (!accessResolved) return
    if (!fullAccess && (!allowedCcIds || allowedCcIds.length === 0)) return

    setAnoChanging(true)
    const timer = setTimeout(() => {
      loadData()
    }, ANO_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [accessResolved, fullAccess, allowedCcIds, ano, loadData])

  const centrosVisiveis = useMemo(
    () => filterCentrosDeCustoForUser(centros, allowedCcIds),
    [centros, allowedCcIds],
  )

  const distribuidoPorCc = useMemo(
    () => aggregateDistribuidoPorCc(dotAnualRows),
    [dotAnualRows],
  )

  const orcadoPorCc = useMemo(
    () => aggregateOrcadoPorCc(dotAnualRows),
    [dotAnualRows],
  )

  const programadoPorCc = useMemo(
    () => aggregateProgramadoPorCc(orcDotRows, ano),
    [orcDotRows, ano],
  )

  const comprometidoPorCc = useMemo(
    () => aggregateComprometidoPorCc(eventoRows, ano),
    [eventoRows, ano],
  )

  const liberadoPorCc = useMemo(
    () => aggregateLiberadoPorCc(bdroRows, ano),
    [bdroRows, ano],
  )

  const pagoPorCc = useMemo(
    () => aggregatePagoPorCc(movRows, ano),
    [movRows, ano],
  )

  const todosCcSummaries = useMemo(
    () => enrichCcSummaries(
      buildCcSummaries({
        centros: centrosVisiveis,
        orcCCustoRows,
        distribuidoPorCc,
        orcadoPorCc,
        programadoPorCc,
        comprometidoPorCc,
        liberadoPorCc,
        pagoPorCc,
        selectedCcId: null,
      }),
      dotAnualRows,
    ).filter(hasCcEsteiraData),
    [
      centrosVisiveis,
      orcCCustoRows,
      distribuidoPorCc,
      orcadoPorCc,
      programadoPorCc,
      comprometidoPorCc,
      liberadoPorCc,
      pagoPorCc,
      dotAnualRows,
    ],
  )

  const contratosPorItem = useMemo(
    () => buildContratosPorItem(contrFornRows),
    [contrFornRows],
  )

  const itemRows = useMemo(
    () => buildItemEsteiraRows({
      ano,
      dotAnualRows,
      orcDotRows,
      eventoRows,
      bdroRows,
      movRows,
      taxonomyRows,
      contratosPorItem,
    }),
    [ano, dotAnualRows, orcDotRows, eventoRows, bdroRows, movRows, taxonomyRows, contratosPorItem],
  )

  const ccSummariesFiltrados = useMemo(() => {
    if (!ccToolbarFilter) return todosCcSummaries
    return todosCcSummaries.filter(s => String(s.ccId) === String(ccToolbarFilter))
  }, [todosCcSummaries, ccToolbarFilter])

  const visibleCards = useMemo(
    () => buildDrillCards({
      drillPath,
      itemRows,
      ccSummaries: ccSummariesFiltrados,
    }).filter(hasDrillEsteiraData),
    [drillPath, itemRows, ccSummariesFiltrados],
  )

  const visibleCardsFiltrados = useMemo(() => {
    const q = buscaToolbar.trim().toLowerCase()
    if (!q) return visibleCards
    return visibleCards.filter(card => {
      const nome = String(card.cardNome || '').toLowerCase()
      const tipo = String(CARD_TYPE_BADGES[card.cardType] || '').toLowerCase()
      const cc = String(card.ccNome || '').toLowerCase()
      return nome.includes(q) || tipo.includes(q) || cc.includes(q)
    })
  }, [visibleCards, buscaToolbar])

  const visibleCardsOrdenados = useMemo(
    () => sortBudgetCards(visibleCardsFiltrados, cardsSortBy, cardsSortDir),
    [visibleCardsFiltrados, cardsSortBy, cardsSortDir],
  )

  const toolbarEsteiraTotais = useMemo(() => {
    const scoped = ccToolbarFilter
      ? todosCcSummaries.filter(s => String(s.ccId) === String(ccToolbarFilter))
      : todosCcSummaries
    return aggregateEsteiraTotais(scoped)
  }, [todosCcSummaries, ccToolbarFilter])

  const cardsDrillLevel = getDrillLevel(drillPath)
  const totalCentros = todosCcSummaries.length

  const chartSummaries = useMemo(
    () => visibleCardsOrdenados.map(card => ({ ...card, ccNome: card.cardNome })),
    [visibleCardsOrdenados],
  )

  const cardsCountLabel = useMemo(() => {
    const total = visibleCards.length
    const shown = visibleCardsFiltrados.length
    const suffix = buscaToolbar.trim() && shown !== total ? ` (${shown} de ${total})` : ''
    if (cardsDrillLevel === DRILL_TYPES.CC) {
      return `${shown}${ccToolbarFilter ? ` de ${totalCentros}` : ''} centro(s) de custo${suffix}`
    }
    const title = DRILL_LEVEL_TITLES[cardsDrillLevel]?.toLowerCase() || 'registros'
    return `${shown} ${title}${suffix}`
  }, [cardsDrillLevel, visibleCardsFiltrados.length, visibleCards.length, ccToolbarFilter, totalCentros, buscaToolbar])
  const cardsLoading = loading || anoChanging

  const resetDrill = () => {
    setDrillPath([])
    setDetalhamentoOrcItemId('')
  }

  const handleBuscaToolbarChange = (e) => {
    setBuscaToolbar(e.target.value)
  }

  const handleAnoChange = (e) => {
    const next = Number(e.target.value) || BUDGET_DASHBOARD_DEFAULT_ANO
    setAno(next)
    setAnoChanging(true)
    setDetalhamentoCcId('')
    resetDrill()
  }

  const handleAnoAtual = () => {
    setAno(currentYear())
    setAnoChanging(true)
    setDetalhamentoCcId('')
    resetDrill()
  }

  const handleCcToolbarChange = (e) => {
    setCcToolbarFilter(e.target.value)
    setDetalhamentoCcId('')
    resetDrill()
  }

  const handleDrillBack = () => {
    setDrillPath(prev => prev.slice(0, -1))
    setDetalhamentoOrcItemId('')
  }

  const handleDrillToIndex = (index) => {
    setDrillPath(prev => prev.slice(0, index + 1))
    setDetalhamentoOrcItemId('')
  }

  const handleOpenItemModal = ({ orcItemId, itemLabel }) => {
    setItemModal({ open: true, orcItemId, itemLabel: itemLabel || `Item #${orcItemId}` })
  }

  const handleOpenCreateContrato = (orcItemId) => {
    if (!orcItemId) return
    setCreateContratoModal({ open: true, orcItemId })
    setItemModal({ open: false, orcItemId: null, itemLabel: '' })
  }

  const handleContratoCreated = () => {
    loadData()
  }

  const handleCardClick = (card) => {
    if (card.cardType === DRILL_TYPES.ITEM) {
      handleOpenItemModal({ orcItemId: card.orcItemId, itemLabel: card.cardNome })
      return
    }

    const node = buildDrillNodeFromCard(card)
    if (!node) return

    setDrillPath(prev => [...prev, node])
    setDetalhamentoOrcItemId('')

    if (card.cardType === DRILL_TYPES.CC) {
      setDetalhamentoCcId(String(card.ccId))
    } else if (drillPath.find(n => n.type === DRILL_TYPES.CC)) {
      setDetalhamentoCcId(String(drillPath.find(n => n.type === DRILL_TYPES.CC).id))
    }
  }

  const cardSubtitle = (card) => {
    if (card.cardType === DRILL_TYPES.CC) {
      return `${card.qtdItens} item(ns) com dotação em ${ano}`
    }
    if (card.cardType === DRILL_TYPES.ITEM) {
      return card.ccNome ? `${card.ccNome} · ${ano}` : `Item orçamentário · ${ano}`
    }
    return `${card.qtdItens} item(ns) agregado(s)`
  }

  const renderDrillCard = (card) => {
    const isSelected = card.cardType === DRILL_TYPES.ITEM
      ? String(detalhamentoOrcItemId) === String(card.orcItemId)
      : drillPath.some(n => String(n.id) === String(
        card.cardType === DRILL_TYPES.CC ? card.ccId
          : card.cardType === DRILL_TYPES.RUBRICA ? card.rubricaId
            : card.subrubricaId,
      ))
    const acimaDoTeto = card.estouro
    const orcadoValor = Number(card.orcado || 0)

    return (
      <Col key={card.cardId} lg={4} md={6} sm={12} className="mb-4">
        <Card
          className={`h-100 shadow-sm ${isSelected ? 'border-primary' : ''}`}
          style={{ cursor: 'pointer', transition: 'box-shadow 0.15s ease' }}
          onClick={() => handleCardClick(card)}
        >
          <CardBody>
            <div className="d-flex align-items-start mb-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  backgroundColor: '#eef0f7',
                  color: '#5664d2',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                {(card.cardNome || '?').charAt(0)}
              </div>
              <div className="flex-grow-1 min-width-0">
                <div className="text-muted small mb-1">{CARD_TYPE_BADGES[card.cardType]}</div>
                <div className="d-flex align-items-baseline justify-content-between mb-1" style={{ gap: 12 }}>
                  <h6
                    className="mb-0 flex-grow-1 min-width-0"
                    style={{ lineHeight: 1.35, wordBreak: 'break-word' }}
                  >
                    {card.cardNome}
                  </h6>
                  <div
                    className="text-right"
                    style={{
                      flexShrink: 0,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#273351',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {orcadoValor > 0 ? formatBudgetMoney(orcadoValor) : '—'}
                  </div>
                </div>
                <div className="text-muted small">{cardSubtitle(card)}</div>
              </div>
              <div className="d-flex flex-wrap justify-content-end ml-1" style={{ gap: 4 }}>
                {acimaDoTeto && (
                  <Badge color="danger" pill style={{ fontSize: '0.65rem' }}>Estouro</Badge>
                )}
                {card.cardType === DRILL_TYPES.CC && card.disponivelDistribuir > 0 && card.orcado > 0 && (
                  <Badge color="secondary" pill style={{ fontSize: '0.65rem' }}>Saldo</Badge>
                )}
              </div>
            </div>

            <BudgetCardEsteiraBars card={card} />
          </CardBody>
        </Card>
      </Col>
    )
  }

  if (!user) {
    return null
  }

  if (accessResolved && !fullAccess && (!allowedCcIds || allowedCcIds.length === 0)) {
    return <Redirect to="/dashboard" />
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Dashboard Orçamentário"
          breadcrumbItems={[{ title: 'Financeiro', link: '/financeiro/orcamento-dashboard' }]}
        />

        <Row className="mb-4">
          <Col>
            <div
              className="d-flex justify-content-between align-items-end flex-wrap"
              style={{ gap: 12 }}
            >
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#6c757d' }}>Ano:</div>
                  <input
                    type="number"
                    min={2020}
                    max={2100}
                    value={ano}
                    onChange={handleAnoChange}
                    disabled={cardsLoading}
                    style={{ ...filterInputStyle, width: 100 }}
                  />
                  <div style={{ fontSize: 12, color: '#6c757d' }}>Centro de custo:</div>
                  <select
                    value={ccToolbarFilter}
                    onChange={handleCcToolbarChange}
                    disabled={cardsLoading || centrosVisiveis.length === 0}
                    style={filterInputStyle}
                  >
                    <option value="">Todos</option>
                    {centrosVisiveis.map(cc => (
                      <option key={cc.value} value={cc.value}>{cc.label}</option>
                    ))}
                  </select>
                  {ccToolbarFilter && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setCcToolbarFilter('')}
                    >
                      Todos
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleAnoAtual}
                    disabled={cardsLoading}
                  >
                    Ano atual
                  </button>
                </div>
              </div>

              {totalCentros > 0 && !cardsLoading && (
                <div className="text-muted small">
                  {cardsCountLabel}
                  {' '}
                  em
                  {' '}
                  {ano}
                </div>
              )}
              {!fullAccess && (
                <div className="text-muted small">
                  Exibindo CCs dos projetos que você coordena.
                </div>
              )}
            </div>
          </Col>
        </Row>

        {!cardsLoading && totalCentros > 0 && (
          <BudgetEsteiraSummaryCards totals={toolbarEsteiraTotais} />
        )}

        {!cardsLoading && loadWarnings.includes('orcDotAnual') && (
          <div className="alert alert-warning">
            Tabela
            {' '}
            <code>orcDotAnual</code>
            {' '}
            indisponível ou sem dados para
            {' '}
            {ano}
            .
            {' '}
            Orçado/Distribuído e etapas seguintes podem aparecer incompletos até a migração ser aplicada neste ambiente.
          </div>
        )}

        {cardsLoading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {!cardsLoading && (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: 12 }}>
              <div>
                <h5 style={sectionTitleStyle} className="mb-0">
                  {DRILL_LEVEL_TITLES[cardsDrillLevel] || 'Visão geral'}
                </h5>
                {drillPath.length > 0 && (
                  <div className="text-muted small mt-1">
                    {drillPath.map(n => n.label).join(' › ')}
                  </div>
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                <input
                  type="search"
                  value={buscaToolbar}
                  onChange={handleBuscaToolbarChange}
                  placeholder="Filtrar por nome…"
                  disabled={cardsLoading}
                  style={{ ...filterInputStyle, width: 200 }}
                />
                {(visaoGeralMode === 'cards' || visaoGeralMode === 'lista') && (
                  <>
                    <span className="text-muted small">Ordenar:</span>
                    <select
                      value={cardsSortBy}
                      onChange={e => setCardsSortBy(e.target.value)}
                      style={filterInputStyle}
                    >
                      <option value="cardNome">Nome</option>
                      {CC_ESTEIRA_STEPS.map(step => (
                        <option key={step.key} value={step.key}>{step.label}</option>
                      ))}
                      <option value="disponivel">Disponível</option>
                    </select>
                    <select
                      value={cardsSortDir}
                      onChange={e => setCardsSortDir(e.target.value)}
                      style={{ ...filterInputStyle, minWidth: 120 }}
                    >
                      <option value="asc">Crescente</option>
                      <option value="desc">Decrescente</option>
                    </select>
                  </>
                )}
                <div className="btn-group btn-group-sm">
                  <Button
                    color={visaoGeralMode === 'cards' ? 'primary' : 'light'}
                    style={viewToggleBtnStyle(visaoGeralMode === 'cards')}
                    onClick={() => setVisaoGeralMode('cards')}
                  >
                    Cards
                  </Button>
                  <Button
                    color={visaoGeralMode === 'graficos' ? 'primary' : 'light'}
                    style={viewToggleBtnStyle(visaoGeralMode === 'graficos')}
                    onClick={() => setVisaoGeralMode('graficos')}
                  >
                    Gráficos
                  </Button>
                  <Button
                    color={visaoGeralMode === 'lista' ? 'primary' : 'light'}
                    style={viewToggleBtnStyle(visaoGeralMode === 'lista')}
                    onClick={() => setVisaoGeralMode('lista')}
                  >
                    Lista
                  </Button>
                </div>
                {visaoGeralMode === 'graficos' && (
                  <select
                    value={graficoMetrica}
                    onChange={e => setGraficoMetrica(e.target.value)}
                    style={filterInputStyle}
                  >
                    {CC_ESTEIRA_STEPS.map(step => (
                      <option key={step.key} value={step.key}>{step.label}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {drillPath.length > 0 && (
              <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
                <Button size="sm" color="light" onClick={handleDrillBack}>
                  <i className="ri-arrow-left-line mr-1" />
                  Voltar
                </Button>
                <Button
                  size="sm"
                  color="link"
                  className="p-0 text-decoration-none"
                  onClick={resetDrill}
                >
                  Centros de custo
                </Button>
                {drillPath.map((node, idx) => (
                  <React.Fragment key={`${node.type}-${node.id}`}>
                    <span className="text-muted">›</span>
                    <Button
                      size="sm"
                      color="link"
                      className="p-0 text-decoration-none"
                      onClick={() => handleDrillToIndex(idx)}
                    >
                      {node.label}
                    </Button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {visaoGeralMode === 'cards' && (
              <Row>
                {visibleCardsOrdenados.map(card => renderDrillCard(card))}
              </Row>
            )}

            {visaoGeralMode === 'graficos' && (
              <Card className="border shadow-sm mb-4">
                <CardBody>
                  <BudgetVisaoGeralChart
                    ccSummaries={chartSummaries}
                    graficoMetrica={graficoMetrica}
                  />
                </CardBody>
              </Card>
            )}

            {visaoGeralMode === 'lista' && (
              <Card className="border shadow-sm mb-4">
                <CardBody className="p-2">
                  <BudgetVisaoGeralTable
                    cards={visibleCardsOrdenados}
                    drillLevel={cardsDrillLevel}
                    drillPath={drillPath}
                    selectedOrcItemId={detalhamentoOrcItemId}
                    onRowClick={handleCardClick}
                  />
                </CardBody>
              </Card>
            )}
          </>
        )}

        {!cardsLoading && totalCentros > 0 && visibleCardsFiltrados.length === 0 && (visaoGeralMode === 'cards' || visaoGeralMode === 'lista') && (
          <div className="text-center text-muted py-5">
            {buscaToolbar.trim()
              ? 'Nenhum registro corresponde ao filtro de busca.'
              : drillPath.length > 0
                ? 'Nenhum registro neste nível para a seleção atual.'
                : 'Nenhum centro de custo para o filtro selecionado.'}
          </div>
        )}

        {!cardsLoading && totalCentros === 0 && (
          <div className="text-center text-muted py-5">
            Nenhum centro de custo com orçamento cadastrado neste ano.
          </div>
        )}

        {!cardsLoading && totalCentros > 0 && (
          <div id="budget-detalhamento" className="mt-4 pt-2">
            <h5 style={sectionTitleStyle}>Detalhamento</h5>
            <BudgetDetalhamentoPanel
              ano={ano}
              centros={centrosVisiveis}
              dotAnualRows={dotAnualRows}
              eventoRows={eventoRows}
              selectedCcId={detalhamentoCcId}
              selectedOrcItemId={detalhamentoOrcItemId}
              onCcIdChange={setDetalhamentoCcId}
              onOrcItemIdChange={setDetalhamentoOrcItemId}
              onOpenItemModal={handleOpenItemModal}
            />
          </div>
        )}

        <BudgetItemModal
          isOpen={itemModal.open}
          onClose={() => setItemModal({ open: false, orcItemId: null, itemLabel: '' })}
          orcItemId={itemModal.orcItemId}
          itemLabel={itemModal.itemLabel}
          onOpenCreateContrato={handleOpenCreateContrato}
        />

        <BudgetCreateContratoModal
          isOpen={createContratoModal.open}
          onClose={() => setCreateContratoModal({ open: false, orcItemId: null })}
          orcItemId={createContratoModal.orcItemId}
          onSuccess={handleContratoCreated}
        />
      </Container>
    </div>
  )
}

const mapStateToProps = state => ({ auth: state.auth })

export default connect(mapStateToProps)(BudgetDashboard)
